import React, { useEffect, useMemo, useState } from "react"

import Button from "../../../../components/ui/button/button"
import Checkbox from "../../../../components/ui/checkbox/checkbox"
import Input from "../../../../components/ui/inputs/input"
import Select from "../../../../components/ui/select/select"
import Textarea from "../../../../components/ui/inputs/textarea"
import Toggle from "../../../../components/ui/toggle/toggle"
import WindowCard from "../../../../components/ui/window-card/window-card"
import { SaveIcon } from "../../../../utils/svgs"

import { TEE_SHEET_CONFIG } from "./config"
import {
	getEnabledSectionIds,
	mergeTeeSheetSnapshot,
	setSectionEnabled,
	updateQuestionAnswer,
} from "./model"

const snapshotKey = (snapshot) => JSON.stringify(snapshot || {})

const SECTION_NUMBER_PREFIXES = {
	about_the_trip: "2",
	accommodation: "3",
	golf: "4",
	flights: "5",
	transfers_and_car_hire: "6",
	close_out: "7",
	statements: "9",
}

function TeeSheet({
	teeSheet,
	client,
	onSave,
	saving,
}) {
	const mergedFromProps = useMemo(
		() => mergeTeeSheetSnapshot(teeSheet, TEE_SHEET_CONFIG),
		[teeSheet]
	)
	const [draft, setDraft] = useState(mergedFromProps)
	const [baseline, setBaseline] = useState(snapshotKey(mergedFromProps))

	useEffect(() => {
		setDraft(mergedFromProps)
		setBaseline(snapshotKey(mergedFromProps))
	}, [mergedFromProps])

	const enabledSectionIds = useMemo(() => new Set(getEnabledSectionIds(draft)), [draft])
	const isDirty = snapshotKey(draft) !== baseline

	const updateAnswer = (sectionId, questionId, answer) => {
		setDraft((current) => updateQuestionAnswer(current, sectionId, questionId, answer))
	}

	const updateAdditionalNotes = (additionalNotes) => {
		setDraft((current) => ({
			...current,
			additional_notes: additionalNotes,
		}))
	}

	const toggleSection = (sectionId, enabled) => {
		setDraft((current) => setSectionEnabled(current, sectionId, enabled))
	}

	const save = async () => {
		const saved = await onSave(draft)
		if (saved) {
			setBaseline(snapshotKey(saved))
			setDraft(saved)
		}
	}

	const renderSection = (section) => {
		const enabled = enabledSectionIds.has(section.id)
		const sectionNumberPrefix = SECTION_NUMBER_PREFIXES[section.id]
		return (
			<WindowCard
				key={section.id}
				id={`tee-sheet-block-${section.id}`}
				className={[
					"tee-sheet-section",
					section.id === "enquiry_confirmation" && "is-confirmation",
					section.id === "about_the_trip" && "is-about-trip",
					section.id === "accommodation" && "is-accommodation",
					section.id === "golf" && "is-golf",
					section.id === "flights" && "is-flights",
					section.id === "transfers_and_car_hire" && "is-transfers-and-car-hire",
					section.id === "close_out" && "is-close-out",
					section.id === "statements" && "is-statements",
					enabled ? "is-enabled" : "is-disabled",
				].filter(Boolean).join(" ")}
				title={section.title}
				subtitle={section.description}>
				{section.triggerQuestionId && (
					<div className="tee-sheet-section-toggle">
						<Toggle
							label="Discuss this section"
							labelAfter
							on={enabled}
							onClick={() => toggleSection(section.id, !enabled)}
						/>
					</div>
				)}

				{enabled ? (
					<div className="tee-sheet-question-list">
						{section.questions.map((question, questionIndex) => {
							const answerOf = (questionId) =>
								section.questions.find((q) => q.id === questionId)?.answer
							const dependency = question.dependsOn
							const dependencyMet = dependency
								? answerOf(dependency.questionId) === dependency.value
								: true
							const blocker = question.disabledWhen
							const blockerActive = blocker
								? answerOf(blocker.questionId) === blocker.value
								: false
							return (
								<TeeSheetQuestion
									key={question.id}
									sectionId={section.id}
									question={question}
									questionNumber={sectionNumberPrefix ? `${sectionNumberPrefix}.${questionIndex + 1}` : null}
									disabled={!dependencyMet || blockerActive}
									client={client}
									onChange={updateAnswer}
								/>
							)
						})}
					</div>
				) : (
					<div className="tee-sheet-disabled-note">
						<p>Enable this section if the client brings it up on the call.</p>
					</div>
				)}
			</WindowCard>
		)
	}

	return (
		<div className="tee-sheet-workspace">
			<div className="tee-sheet-top-save">
				<Button
					small
					label="Save"
					icon={<SaveIcon />}
					loading={saving}
					loadingText="Saving..."
					disabled={saving || !isDirty}
					onClick={save}
				/>
			</div>

			<div className="tee-sheet-main">
				{draft.sections.filter((section) => section.id !== "statements").map((section) => renderSection(section))}

				<WindowCard
					id="tee-sheet-block-additional_notes"
					className="tee-sheet-section is-additional-notes is-enabled"
					title="Additional notes">
					<Textarea
						value={draft.additional_notes || ""}
						onChange={updateAdditionalNotes}
						placeholder="Add freeform notes from the call..."
						rows={8}
						showLabel
					/>
				</WindowCard>

				{draft.sections.filter((section) => section.id === "statements").map((section) => renderSection(section))}

				<div className="tee-sheet-bottom-save">
					<Button
						small
						label="Save"
						icon={<SaveIcon />}
						loading={saving}
						loadingText="Saving..."
						disabled={saving || !isDirty}
						onClick={save}
					/>
				</div>
			</div>
		</div>
	)
}

function TeeSheetQuestion({ sectionId, question, questionNumber, disabled, client, onChange }) {
	const stackedInput = question.type !== "boolean" && question.type !== "confirm_contact"

	return (
		<div
			className={[
				"tee-sheet-question",
				stackedInput && "is-stacked-input",
				question.readOnly && "is-read-only",
				disabled && "is-dependency-disabled",
				question.type === "confirm_contact" && "is-confirm-contact",
			].filter(Boolean).join(" ")}
			aria-disabled={disabled || undefined}>
			<div className="tee-sheet-question-copy">
				<label>
					{questionNumber && <span className="tee-sheet-question-number">{questionNumber}</span>}
					{question.question}
				</label>
				{question.required && <span>Required</span>}
				{question.historical && <span>Historical</span>}
				{question.helpText && <p>{question.helpText}</p>}
			</div>
			<TeeSheetQuestionInput
				sectionId={sectionId}
				question={question}
				client={client}
				onChange={onChange}
			/>
			{disabled && <div className="tee-sheet-question-disabled-overlay" aria-hidden="true" />}
		</div>
	)
}

function TeeSheetQuestionInput({ sectionId, question, client, onChange }) {
	const readOnly = question.readOnly
	const update = (value) => onChange(sectionId, question.id, value)

	if (question.type === "confirm_contact") {
		const answer = question.answer && typeof question.answer === "object"
			? question.answer
			: { confirmed: false, overrides: {} }
		const overrides = answer.overrides || {}
		const updateOverride = (fieldId, value) =>
			update({ ...answer, overrides: { ...overrides, [fieldId]: value } })
		const toggleConfirmed = () =>
			update({ ...answer, confirmed: answer.confirmed !== true })
		return (
			<>
				<Toggle
					label={answer.confirmed === true ? "Done" : "No"}
					labelAfter
					on={answer.confirmed === true}
					onClick={() => !readOnly && toggleConfirmed()}
				/>
				<div className="tee-sheet-confirm-contact-fields">
					{(question.contactFields || []).map((field) => (
						<div key={field.id} className="tee-sheet-confirm-contact-field">
							<span className="tee-sheet-confirm-contact-on-file">
								{client?.[field.clientField] || "—"}
							</span>
							<Input
								value={overrides[field.id] || ""}
								onChange={(value) => updateOverride(field.id, value)}
								readOnly={readOnly}
								placeholder={`Override ${field.label.toLowerCase()} if different...`}
							/>
						</div>
					))}
				</div>
			</>
		)
	}

	if (question.type === "textarea") {
		return (
			<Textarea
				value={question.answer || ""}
				onChange={update}
				readOnly={readOnly}
				placeholder="Type notes..."
				rows={4}
			/>
		)
	}

	if (question.type === "select") {
		return (
			<Select
				value={question.answer || ""}
				selected={question.answer || ""}
				onSelect={(option) => update(option.option)}
				options={question.options || {}}
				readOnly={readOnly}
				placeholder="Select an answer"
			/>
		)
	}

	if (question.type === "boolean") {
		const labels = question.labels || { on: "Yes", off: "No" }
		return (
			<Toggle
				label={question.answer ? labels.on : labels.off}
				on={question.answer === true}
				labelAfter
				onClick={() => !readOnly && update(question.answer !== true)}
			/>
		)
	}

	if (question.type === "multi_select") {
		const values = Array.isArray(question.answer) ? question.answer : []
		return (
			<div className="tee-sheet-checkbox-list">
				{Object.entries(question.options || {}).map(([value, label]) => (
					<Checkbox
						key={value}
						label={label}
						checked={values.includes(value)}
						onClick={() => {
							if (readOnly) return
							update(
								values.includes(value)
									? values.filter((item) => item !== value)
									: [...values, value]
							)
						}}
					/>
				))}
			</div>
		)
	}

	return (
		<Input
			type={question.type === "number" ? "number" : "text"}
			value={question.answer || ""}
			onChange={update}
			readOnly={readOnly}
			symbol={question.prefix || undefined}
			placeholder="Type answer..."
		/>
	)
}

export default TeeSheet
